'use client';

import { useTranslations } from 'next-intl';
// eslint-disable-next-line no-restricted-imports
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useTabIndicator } from '@/hooks/useTabIndicator';
import { cn } from '@/utils/cn';
import { GameTabsMap } from '../utils/detailed-game.utils';

const DEFAULT_HEADER_OFFSET = 64;

interface GameTabsProps {
  className?: string;
  tabs: { id: string; title: string }[];
}

export const GameTabs = ({ tabs, className }: GameTabsProps) => {
  const t = useTranslations('game');

  const [activeTab, setActiveTab] = useState(() => tabs[0] ? `#${tabs[0].id}` : `#${GameTabsMap.gallery.id}`);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isManualScrolling, setIsManualScrolling] = useState(false);

  const activeIndex = tabs.findIndex(tab => `#${tab.id}` === activeTab);
  const { tabRef, indicatorStyle } = useTabIndicator<HTMLAnchorElement>(activeIndex, { deps: [tabs] });

  const tabContainerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedEntriesRef = useRef<Map<string, IntersectionObserverEntry>>(new Map());
  const activeTabRafRef = useRef<number | null>(null);

  const getCurrentScrollOffset = () => {
    const headerHeight = DEFAULT_HEADER_OFFSET;
    const tabsContainer = tabContainerRef.current;
    const tabsHeight = tabsContainer ? tabsContainer.offsetHeight : 60;
    return headerHeight + tabsHeight;
  };

  const handleScrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, anchor: string) => {
    e.preventDefault();
    setActiveTab(anchor);
    setIsManualScrolling(true);

    const section = document.querySelector(anchor) as HTMLElement;
    if (section) {
      const totalOffset = getCurrentScrollOffset();
      const targetY = section.offsetTop - totalOffset;

      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: 'smooth',
      });

      setTimeout(() => {
        setIsManualScrolling(false);
      }, 800);
    }
  };

  useEffect(() => {
    const firstTab = tabs[0];
    if (!firstTab) {
      return;
    }

    const hasActiveTabInList = tabs.some(tab => `#${tab.id}` === activeTab);
    if (!hasActiveTabInList) {
      setActiveTab(`#${firstTab.id}`);
    }
  }, [tabs, activeTab]);

  useEffect(() => {
    const handleFullscreen = (e: Event) => {
      setIsFullscreen((e as CustomEvent).detail === true);
    };
    window.addEventListener('app:fullscreen', handleFullscreen);
    return () => window.removeEventListener('app:fullscreen', handleFullscreen);
  }, []);

  useEffect(() => {
    const totalOffset = getCurrentScrollOffset();
    observedEntriesRef.current.clear();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (isManualScrolling) {
          return;
        }

        entries.forEach((entry) => {
          observedEntriesRef.current.set(entry.target.id, entry);
        });

        const visibleEntries = Array.from(observedEntriesRef.current.values()).filter(entry => entry.isIntersecting);

        if (!visibleEntries.length) {
          return;
        }

        const closestToOffset = visibleEntries.reduce((closest, entry) => {
          const closestDistance = Math.abs(closest.boundingClientRect.top - totalOffset);
          const entryDistance = Math.abs(entry.boundingClientRect.top - totalOffset);
          return entryDistance < closestDistance ? entry : closest;
        });

        const nextActiveTab = `#${closestToOffset.target.id}`;

        if (activeTabRafRef.current) {
          cancelAnimationFrame(activeTabRafRef.current);
        }

        activeTabRafRef.current = requestAnimationFrame(() => {
          setActiveTab(prev => (prev === nextActiveTab ? prev : nextActiveTab));
          activeTabRafRef.current = null;
        });
      },
      {
        rootMargin: `-${totalOffset}px 0px -50% 0px`,
        threshold: 0,
      },
    );

    tabs.forEach((tab) => {
      const section = document.querySelector(`#${tab.id}`);
      if (section) {
        observerRef.current?.observe(section);
      }
    });

    return () => {
      observerRef.current?.disconnect();
      if (activeTabRafRef.current) {
        cancelAnimationFrame(activeTabRafRef.current);
        activeTabRafRef.current = null;
      }
      observedEntriesRef.current.clear();
    };
  }, [tabs, isManualScrolling]);

  return (
    <div
      className={cn(
        'max-w-218.5 mb-13 3xl:max-w-full',
        isFullscreen ? 'relative' : 'sticky top-15.5 z-30 bg-c900 py-2.5 -mx-4 px-4 md:mx-0 md:px-0',
        className,
      )}
    >
      <div
        ref={tabContainerRef}
        className="relative"
      >
        <div className="relative overflow-hidden md:mx-0">
          <ul
            className="flex py-0.5 relative overflow-x-auto hide-scrollbar md:overflow-x-visible"
          >

            <div
              className={cn(
                'absolute top-0 h-full rounded-2xl transition-all duration-500 pointer-events-none',
                'bg-main-back-gradient p-px',
                !isFullscreen && 'z-10',
                indicatorStyle.width ? 'opacity-100' : 'opacity-0',
              )}
              style={{
                left: indicatorStyle.left,
                width: indicatorStyle.width,
              }}
            >
              <div className="bg-main-800 w-full h-full rounded-2xl" />
            </div>
            {tabs.map((tab, i) => (
              <li key={tab.title}>
                <Link
                  href={`#${tab.id}`}
                  ref={(el) => {
                    tabRef.current[i] = el;
                  }}
                  className={cn(
                    'px-4.5 py-2 whitespace-nowrap inline-block relative rounded-xl transition-colors duration-300',
                    !isFullscreen && 'z-20',
                    activeTab === `#${tab.id}`
                      ? 'text-white'
                      : 'text-c300 lg:hover:text-light lg:hover:bg-main-800',
                  )}
                  onClick={e => handleScrollToSection(e, `#${tab.id}`)}
                >
                  {t(tab.title)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
